// Types for the Automated Document Request System

export type DocumentRequestStatus = 'draft' | 'active' | 'completed' | 'cancelled';
export type FamilyRequestStatus = 'pending' | 'submitted' | 'approved' | 'rejected';
export type ReminderType = 'initial' | 'reminder_1' | 'reminder_2' | 'final';
export type EmailStatus = 'sent' | 'delivered' | 'opened' | 'failed';

// Core document request interface
export interface DocumentRequest {
  id: string;
  organization_id: string;
  title: string;
  description?: string;
  deadline?: string; // ISO date string
  created_by: string;
  status: DocumentRequestStatus;
  template_id?: string; // For form-based requests
  signatureapi_envelope_id?: string; // SignatureAPI envelope ID
  signing_mode: 'upload' | 'form'; // How documents should be submitted
  created_at: string;
  updated_at: string;
}

// Document request with family assignment tracking
export interface DocumentRequestFamily {
  id: string;
  request_id: string;
  family_id: string;
  status: FamilyRequestStatus;
  completed_at?: string;
  created_at: string;
  // Joined data
  family_name?: string;
  family?: {
    id: string;
    name: string;
  };
}

// Document submission
export interface DocumentSubmission {
  id: string;
  request_id: string;
  family_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  submitted_at: string;
  reviewed_by?: string;
  reviewed_at?: string;
  notes?: string;
  // Joined data
  family_name?: string;
  reviewer_name?: string;
}

// Reminder tracking
export interface RequestReminder {
  id: string;
  request_id: string;
  family_id: string;
  sent_at: string;
  reminder_type: ReminderType;
  email_status: EmailStatus;
}

// Extended document request with statistics and related data
export interface DocumentRequestWithStats extends DocumentRequest {
  stats: DocumentRequestStats;
  families?: DocumentRequestFamily[];
  submissions?: DocumentSubmission[];
  total_families: number;
  submitted_count: number;
  pending_count: number;
}

// Statistics for document request completion
export interface DocumentRequestStats {
  total_families: number;
  submitted_count: number;
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  completion_percentage: number;
}

// Form data for creating a new document request
export interface CreateDocumentRequestData {
  title: string;
  description?: string;
  deadline?: string; // ISO date string
  family_ids: string[]; // Array of family IDs to include
  organization_id: string;
  created_by: string;
  template_id?: string; // For form-based requests
  signing_mode: 'upload' | 'form'; // How documents should be submitted
  // Enhanced fields for manual form uploads
  form_file_path?: string; // Path to uploaded PDF form for manual workflow
  form_file_name?: string; // Original filename of uploaded form
}

// Form data for updating a document request
export interface UpdateDocumentRequestData {
  title?: string;
  description?: string;
  deadline?: string;
  status?: DocumentRequestStatus;
}

// Family selection for document requests
export interface FamilyOption {
  id: string;
  name: string;
  parent_count?: number;
  student_count?: number;
  has_pending_requests?: boolean;
  children?: Array<{
    id: string;
    first_name: string;
    last_name: string;
    preferred_name?: string;
  }>;
}

// Email template data for document requests
export interface DocumentRequestEmailData {
  request_title: string;
  request_description?: string;
  deadline?: string;
  family_name: string;
  children_names?: string[];
  upload_link: string;
  organization_name: string;
  organization_logo?: string;
}

// Document request filters for admin dashboard
export interface DocumentRequestFilters {
  status?: DocumentRequestStatus[];
  deadline_from?: string;
  deadline_to?: string;
  search_query?: string;
  created_by?: string;
  family_id?: string;
}

// API response types
export interface DocumentRequestListResponse {
  data: DocumentRequestWithStats[];
  count: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface DocumentRequestDetailsResponse {
  request: DocumentRequestWithStats;
  families: DocumentRequestFamily[];
  submissions: DocumentSubmission[];
  reminders: RequestReminder[];
}

// File upload validation
export interface FileUploadValidation {
  max_size_mb: number;
  allowed_types: string[];
  max_files_per_request: number;
}

// Common document types (can be extended)
export const COMMON_DOCUMENT_TYPES = [
  'Medical Forms',
  'Emergency Contact Information',
  'Photo/Video Release Forms',
  'Liability Waivers',
  'Insurance Information',
  'Allergy/Dietary Information',
  'Enrollment Forms',
  'Payment Authorization',
  'Pick-up Authorization',
  'Background Check Forms'
] as const;

export type CommonDocumentType = typeof COMMON_DOCUMENT_TYPES[number];

// Enhanced form template interface with signature fields
export interface FormTemplate {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  category: 'waiver' | 'medical' | 'emergency' | 'enrollment' | 'media' | 'other';
  template_file_url: string;
  preview_image_url?: string; // Generated preview of the template
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Enhanced template fields
  signature_fields?: SignatureField[];
  form_fields?: FormField[];
  template_settings?: TemplateSettings;
}

// Signature field placement
export interface SignatureField {
  id: string;
  label: string;
  x: number; // X coordinate (percentage of page width)
  y: number; // Y coordinate (percentage of page height)
  width: number; // Width (percentage of page width)
  height: number; // Height (percentage of page height)
  page: number; // Page number (1-based)
  required: boolean;
  signer_role: 'parent' | 'guardian' | 'emergency_contact' | 'admin';
  field_type: 'signature' | 'initial' | 'date_signed';
  tooltip?: string; // Help text for the signer
}

// Form field placement (text inputs, checkboxes, etc.)
export interface FormField {
  id: string;
  label: string;
  x: number; // X coordinate (percentage of page width)
  y: number; // Y coordinate (percentage of page height)
  width: number; // Width (percentage of page width)
  height: number; // Height (percentage of page height)
  page: number; // Page number (1-based)
  required: boolean;
  field_type: 'text' | 'email' | 'phone' | 'date' | 'checkbox' | 'dropdown' | 'textarea';
  placeholder?: string;
  validation?: FieldValidation;
  options?: string[]; // For dropdown fields
  default_value?: string;
  tooltip?: string;
}

// Field validation rules
export interface FieldValidation {
  min_length?: number;
  max_length?: number;
  pattern?: string; // Regex pattern
  error_message?: string;
}

// Template configuration settings
export interface TemplateSettings {
  signing_order: 'sequential' | 'parallel';
  expiration_days: number; // How long the signing link is valid
  reminder_schedule: number[]; // Days after sending to send reminders [1, 3, 7]
  require_authentication: boolean;
  allow_decline: boolean;
  brand_color?: string; // Hex color for signing interface
  custom_message?: string; // Message shown to signers
}

// Template designer state
export interface TemplateDesignerState {
  template_id?: string;
  pdf_url: string;
  pdf_pages: number;
  current_page: number;
  zoom_level: number;
  selected_field?: string;
  signature_fields: SignatureField[];
  form_fields: FormField[];
  is_preview_mode: boolean;
}

// Document request template
export interface DocumentRequestTemplate {
  id: string;
  organization_id: string;
  name: string;
  title: string;
  description: string;
  default_deadline_days: number; // Days from creation
  document_type: CommonDocumentType;
  is_required: boolean;
  created_at: string;
  updated_at: string;
}

// Parent portal document request view
export interface ParentDocumentRequest {
  id: string;
  title: string;
  description?: string;
  deadline?: string;
  status: FamilyRequestStatus;
  submitted_at?: string;
  family_id: string;
  organization_name: string;
  submissions: DocumentSubmission[];
  can_submit: boolean; // Based on deadline and current status
  signing_mode?: 'upload' | 'form'; // How documents should be submitted
  template_id?: string; // For form-based requests
}

// Bulk actions for admin
export interface BulkAction {
  action: 'send_reminder' | 'extend_deadline' | 'mark_complete' | 'cancel_request';
  family_ids: string[];
  request_id: string;
  new_deadline?: string; // For extend_deadline action
  reminder_type?: ReminderType; // For send_reminder action
}

// Analytics data
export interface DocumentRequestAnalytics {
  total_requests: number;
  active_requests: number;
  completion_rate: number;
  average_completion_time_days: number;
  most_requested_documents: Array<{
    document_type: string;
    count: number;
  }>;
  families_with_pending: number;
  overdue_submissions: number;
}